import {JSEncrypt} from 'jsencrypt';
// import CryptoJS from 'crypto-js';
import SHA1 from 'crypto-js/sha1';
import Base64 from 'crypto-js/enc-base64';
import Utf8 from 'crypto-js/enc-utf8';
import Hex from 'crypto-js/enc-hex';
import Latin1 from 'crypto-js/enc-latin1';

// import KJUR from 'jsrsasign';

export enum VerificationTypes {
  SMS = 'OTP',
  PIN = 'PIN',
  CERTIFICATE = 'SIGNATURE',
  TABLECODE = 'TABLECODE',
  NONE = 'NONE',
  EMAILCODE = 'CODE',
  GOOGLECODE = 'GOOGLECODE'
}

export enum CertificateError {
  NeedFile = 'NeedFile',
  NotReadableError = 'NotReadable',
  SecurityError = 'Security',
  NotFoundError = 'NotFound',
  DecryptionError = 'Decryption',
  SignFail = 'SignFail',
  TooBigFile = 'TooBigFile',
  WrongPassword = 'WrongPassword'
}

export class Certificate {
  protected sourceData = '';

  username = '';

  password = '';

  constructor(certificateData?: string) {
    if (certificateData) this.loadCertificate(certificateData);
    // this.username = username;
    // this.password = password;
  }

  messages = {
    [CertificateError.DecryptionError]: 'Не удалось расшифровать сертификат. Проверьте правильность ввода пароля.',
    [CertificateError.SignFail]: 'Не удалось произвести подпись.',
    [CertificateError.TooBigFile]: 'Размер файла слишком велик.',
    [CertificateError.NotFoundError]: 'Сертификат в этом файле не найден.',
    [CertificateError.NeedFile]: 'Файл не найден.',
    [CertificateError.NotReadableError]: 'Не удается открыть файл.',
    [CertificateError.SecurityError]: 'Файл не найден.',
  };

  getPrivateKey(): string {
    return this.sourceData;
  }

  static isFileAPIEnabled(): boolean {
    return !!(window as any).FileReader;
  }

  isLoaded(): boolean {
    return this.sourceData.length > 0;
  }

  isPlainPem() {
    return this.isLoaded()
      && Certificate.validatePemPrivateKey(this.sourceData);
  }

  isNeedPassword(): boolean {
    return this.isLoaded()
      && !Certificate.validatePemPrivateKey(this.sourceData)
      && Certificate.validateEncryptedPemPrivateKey(this.sourceData);
  }

  verificationSign(dataForSign: string, plain = false): string {
    if (dataForSign.length == 0) return '';

    const data = plain ? dataForSign : Base64.parse(dataForSign).toString(Utf8);

    if (data.length == 0) return '';

    const base64sign = this.signBase64(
      this.isPlainPem() ? this.getPrivateKey() : this.decryptCertificate(),
      data);

    return base64sign ? Base64.parse(base64sign).toString(Hex) : '';
  }

  verificationPlainSign(dataForSign: string): string {
    if (dataForSign.length == 0) return '';

    const data = dataForSign;

    if (data.length == 0) return '';

    const result = this.signBase64(this.isPlainPem() ? this.getPrivateKey() : this.decryptCertificate(), data);

    return result || '';

    // console.log('sign-bin',  CryptoJS.enc.Base64.parse(base64sign));
    //
    // return CryptoJS.enc.Base64.parse(base64sign).toString(CryptoJS.enc.Base64);
  }

  signBase64(privateKey: string, message: string): string | false {
    // const sig = new KJUR.crypto.Signature({"alg": "SHA1withRSA"});
    // sig.init(privateKey);
    // sig.updateString(message);
    // return sig.sign();

    const encrypt = new JSEncrypt({});
    encrypt.setPrivateKey(privateKey);
    return encrypt.sign(message, SHA1 as any /* as (str: string) => string */, 'sha1');
    ///
    // let rsa = RSAKey();
    // rsa.readPrivateKeyFromPEMString(privateKey);
    // let hashAlg = 'sha1';
    // return rsa.sign(message, hashAlg);
    ///
    // CryptoJS.AES.sign
  }

  static validatePemPrivateKey(data: string): boolean {
    const d = (`${data}`).replace(/[\n\r]/g, '');
    return (d != null) && (d.length >= 0)
      && (d.match(/-{5}BEGIN RSA PRIVATE KEY-{5}.+-{5}END RSA PRIVATE KEY-{5}/g) != null);
  }

  static validateEncryptedPemPrivateKey(data: string): boolean {
    const lines = (`${data}`).trim().split('\n');
    return lines.length == 2;
  }

  loadCertificate(data: string): void {
    if (Certificate.validatePemPrivateKey(data)) this.sourceData = data;
    else if (Certificate.validateEncryptedPemPrivateKey(data)) this.sourceData = data;
    else this.sourceData = '';
  }

  getData(): string {
    return this.sourceData;
  }

  decryptCertificate(): string {
    const lines = String(this.sourceData).split('\n');
    const encodedPrivateKey = Base64.parse(lines[1].trim());
    const key = SHA1(lines[0].trim() + this.username + this.password);
    const decrypted = this.rc4(key.toString(Latin1), encodedPrivateKey.toString(Latin1));

    if (!decrypted || (String(decrypted).length == 0) ||
      !String(decrypted).match(/BEGIN RSA PRIVATE KEY/))
    {
      throw new Error('Wrong certificate password.');
    }

    return decrypted;
  }

  /*
   * RC4 symmetric cipher encryption/decryption
   *
   * @license Public Domain
   * @param string key - secret key for encryption/decryption
   * @param string str - string to be encrypted/decrypted
   * @return string
   */
  protected rc4(key: string, str: string): string {
    const s: number[] = [];
    let j = 0;
    let x;
    let
      res = '';
    for (let i = 0; i < 256; i++) {
      s[i] = i;
    }
    for (let i = 0; i < 256; i++) {
      j = (j + s[i] + key.charCodeAt(i % key.length)) % 256;
      x = s[i];
      s[i] = s[j];
      s[j] = x;
    }
    let i = 0;
    j = 0;
    for (let y = 0; y < str.length; y++) {
      i = (i + 1) % 256;
      j = (j + s[i]) % 256;
      x = s[i];
      s[i] = s[j];
      s[j] = x;
      res += String.fromCharCode(str.charCodeAt(y) ^ s[(s[i] + s[j]) % 256]);
    }
    return res;
  }
}
